import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import endpoints from '@endpoints';
import { ApiService } from '@services/api/api.service';
import { SimpleResponse } from '@models/api.model';
import { interpolate } from '@helpers/index';
import {
    AutomationFieldType,
    AutomationFieldKind,
    AutomationRuleField,
    AutomationRule,
    AutomationRuleTypeGroup,
} from '@models/automation';
import { AutomationValueKind } from '@models/automation-rules';
import {
    optionValueSetsEqual,
    optionValuesEqual,
} from '@helpers/automation-option-value';

interface AutomationRuleTypeFieldApi {
    kind: number;
    fieldId: number;
    fieldName: string;
    fieldType: number | AutomationFieldType;
    targetColumnName?: string | null;
    valueKind?: string | null;
}

interface AutomationRuleTypeApi {
    id: number;
    inputFields: AutomationRuleTypeFieldApi[];
    outputFields: AutomationRuleTypeFieldApi[];
}

interface AutomationRuleValueApi {
    fieldId: number;
    /** `null` for a wildcard input, which stores no value. */
    value?: string | null;
    isWildcard?: boolean;
}

interface AutomationRuleApi {
    id: number;
    inputs: AutomationRuleValueApi[];
    outputs: AutomationRuleValueApi[];
}

interface WorkflowAutomationApi {
    ruleType: AutomationRuleTypeApi;
    rules: AutomationRuleApi[];
}

interface WorkflowAutomationRulesApiResponse {
    workflowId: number;
    automations: WorkflowAutomationApi[];
}

@Injectable({
    providedIn: 'root',
})
export class AutomationService {
    private readonly _path = 'v1';

    private readonly api = inject(ApiService);

    getAutomationRules(
        workflowId: number,
    ): Observable<AutomationRuleTypeGroup[]> {
        return this.api
            .get(
                this._path +
                    interpolate(endpoints.workflowAutomationRules, {
                        workflowId,
                    }),
            )
            .pipe(
                map(
                    (
                        response: SimpleResponse<WorkflowAutomationRulesApiResponse>,
                    ) => this.toRuleTypeGroups(response.data),
                ),
                catchError(() => of([] as AutomationRuleTypeGroup[])),
            );
    }

    /**
     * One group per rule type. The grouping is kept because chained evaluation
     * runs each rule type at most once and orders them by their field
     * dependencies, neither of which can be expressed on a flat rule list.
     */
    private toRuleTypeGroups(
        data: WorkflowAutomationRulesApiResponse | undefined,
    ): AutomationRuleTypeGroup[] {
        const automations = data?.automations ?? [];

        return automations.map((automation) => {
            const inputFieldsById = new Map(
                (automation.ruleType?.inputFields ?? []).map((field) => [
                    field.fieldId,
                    field,
                ]),
            );
            const outputFieldsById = new Map(
                (automation.ruleType?.outputFields ?? []).map((field) => [
                    field.fieldId,
                    field,
                ]),
            );

            const rules = (automation.rules ?? []).map((rule) => ({
                id: rule.id,
                inputs: this.toRuleFields(rule.inputs, inputFieldsById, true),
                outputs: this.toRuleFields(rule.outputs, outputFieldsById),
            }));

            return {
                ruleTypeId: automation.ruleType?.id ?? 0,
                // Rules can reference fields the rule type metadata does not
                // declare; those still take part in the cascade.
                inputFieldIds: [
                    ...new Set([
                        ...inputFieldsById.keys(),
                        ...rules.flatMap((rule) =>
                            rule.inputs.map((input) => input.fieldId),
                        ),
                    ]),
                ],
                outputFieldIds: [
                    ...new Set([
                        ...outputFieldsById.keys(),
                        ...rules.flatMap((rule) =>
                            rule.outputs.map((output) => output.fieldId),
                        ),
                    ]),
                ],
                rules,
            };
        });
    }

    /**
     * @param allowWildcard - Only inputs can stand for "all values"; an
     * output is always a concrete value, whatever the api sends.
     */
    private toRuleFields(
        values: AutomationRuleValueApi[] | undefined,
        fieldsById: Map<number, AutomationRuleTypeFieldApi>,
        allowWildcard = false,
    ): AutomationRuleField[] {
        return (values ?? []).map((item) => {
            const field = fieldsById.get(item.fieldId);
            return {
                fieldId: item.fieldId,
                fieldName: field?.fieldName ?? '',
                fieldType: this.toFieldType(field?.fieldType),
                fieldKind: this.toFieldKind(field?.kind),
                targetColumnName: field?.targetColumnName ?? null,
                valueKind: this.toValueKind(field?.valueKind),
                // A wildcard input carries no value.
                value: item.value ?? '',
                isWildcard: allowWildcard && item.isWildcard === true,
            };
        });
    }

    /**
     * Only the five standard kinds are mapped; every other kind collapses to
     * `None` and is resolved through `targetColumnName` instead. The kind is
     * not an identity — fields are identified by `fieldId`.
     */
    private toFieldKind(kind: number | undefined): AutomationFieldKind {
        if (kind == null) return AutomationFieldKind.None;

        const allowedKinds = new Set<number>([
            AutomationFieldKind.None,
            AutomationFieldKind.Reason,
            AutomationFieldKind.Station,
            AutomationFieldKind.Fixer,
            AutomationFieldKind.SecondaryFixer,
            AutomationFieldKind.Directions,
        ]);

        return allowedKinds.has(kind)
            ? (kind as AutomationFieldKind)
            : AutomationFieldKind.None;
    }

    /**
     * A field the api sends no value kind for keeps the id based behaviour its
     * values were stored with.
     */
    private toValueKind(
        valueKind: string | null | undefined,
    ): AutomationValueKind | null {
        if (valueKind === AutomationValueKind.Name)
            return AutomationValueKind.Name;
        if (valueKind === AutomationValueKind.Id) return AutomationValueKind.Id;

        return null;
    }

    private toFieldType(
        fieldType: number | AutomationFieldType | undefined,
    ): AutomationFieldType {
        if (fieldType === 'SingleSelect') return 'SingleSelect';
        if (fieldType === 'MultiSelect') return 'MultiSelect';
        if (fieldType === 'FreeText') return 'FreeText';

        if (fieldType === 0) return 'SingleSelect';
        if (fieldType === 1) return 'MultiSelect';

        return 'FreeText';
    }

    /**
     * Pure client-side rule matching.
     *
     * A rule matches when every one of its inputs matches: a concrete input
     * against an equal, populated current value, a wildcard input against any
     * value — including an empty one and a field the screen does not render,
     * which reaches this method as a missing entry.
     *
     * A multi select input means the exact combination of its options, held as
     * a set: it matches the same options in any order and with any repetition,
     * but neither a subset nor a superset of them.
     *
     * The most specific matching rule wins, resolved per output field: among
     * the matching rules that carry that field, the ones with the most
     * concrete (non-wildcard) inputs decide its value. Rules not carrying the
     * field are ignored for it, so a rule can win one field while another
     * rule wins the next. Equally specific rules disagreeing on a field fill
     * nothing for it, leaving the current value untouched.
     *
     * Returns the resolved outputs, or `null` when no rule matches or nothing
     * is left to write. Fields are keyed by `fieldId`, so two fields can never
     * share an entry.
     *
     * Evaluated per rule type: precedence and conflicts are contained in one
     * of them and must not affect the evaluation of the others.
     */
    matchRules(
        group: AutomationRuleTypeGroup,
        currentValues: Map<number, string | null>,
    ): Map<number, string> | null {
        const matchingRules = group.rules.filter((rule) =>
            rule.inputs.every((input) => {
                if (input.isWildcard) return true;
                const currentVal = currentValues.get(input.fieldId);
                if (currentVal == null) return false;

                const valuesEqual =
                    input.fieldType === 'MultiSelect'
                        ? optionValueSetsEqual
                        : optionValuesEqual;

                return valuesEqual(
                    currentVal.toString(),
                    input.value,
                    input.valueKind,
                );
            }),
        );

        if (matchingRules.length === 0) return null;

        /** Candidate values of one output field, by rule specificity. */
        const candidates = new Map<
            number,
            { specificity: number; values: Set<string> }
        >();

        for (const rule of matchingRules) {
            const specificity = rule.inputs.filter(
                (input) => !input.isWildcard,
            ).length;

            for (const output of rule.outputs) {
                const value = output.value ?? '';
                const candidate = candidates.get(output.fieldId);

                if (!candidate || specificity > candidate.specificity) {
                    candidates.set(output.fieldId, {
                        specificity,
                        values: new Set([value]),
                    });
                } else if (specificity === candidate.specificity) {
                    candidate.values.add(value);
                }
            }
        }

        const outputMap = new Map<number, string>();
        candidates.forEach(({ values }, fieldId) => {
            // Equally specific rules disagreeing on this field: fill nothing
            // for it rather than an arbitrary one of their values.
            if (values.size > 1) return;
            outputMap.set(fieldId, [...values][0]);
        });

        return outputMap.size > 0 ? outputMap : null;
    }
}
